'use client';

import { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Button } from '@tranquility-ai/theme-components/button';
import {
  CaretRightIcon,
  FolderOpenIcon,
  CheckIcon,
  DresserIcon,
  PlusIcon,
  ProhibitIcon,
} from '@phosphor-icons/react';
import type { FolderItem, TreeNodeResponse } from '@/shared/cors-provenance-api';
import {
  useProvenanceFolders,
  useProvenanceTree,
  useProvenanceTreeSearch,
} from '@/hooks/useProvenanceTree';
import {
  iconForFileName,
  iconForTreeNode as iconForFileNode,
  pathTail,
  pickCaseRootDirectoryNode,
  treeNodeMeta as nodeMeta,
} from '@/shared/provenance-tree-ui';
import {
  navStackToBreadcrumbItems,
  pathToBreadcrumbItems,
} from '@/shared/evidence-locker-breadcrumbs';
import { Breadcrumbs, type BreadcrumbItem } from '@tranquility-ai/theme-components/breadcrumbs';
import '@tranquility-ai/theme-components/breadcrumbs.css';
import '@tranquility-ai/theme-components/menu.css';
import './evidence-locker-tree.css';
import { relabelCaseFolderSegments } from '@/shared/case-summary';
import { useCaseFolderLabels } from '@/hooks/useCaseFolderName';
import { useTimePilotT } from '@/i18n/useTimePilotT';
import { useEvidenceLockerAppearance } from './evidence-locker-appearance';
import { lockerTreeClasses } from './evidence-locker-tone';

/**
 * Public ref API: lets the parent breadcrumb back to a particular node when
 * the user clicks one of the picked-file chips below the chat bar. Mirrors
 * the zprovenance ``TreeBrowserHandle``.
 */
export interface EvidenceLockerTreeHandle {
  /** Navigate the tree so the given node is in view (and clear search). */
  navigateToNode: (node: TreeNodeResponse) => void;
  /**
   * Return the URLs for every currently-displayed row, split by kind. Used by
   * the parent's "Select All" handler so it can populate
   * ``selectedEvidence`` / ``selectedScopes`` with exactly what the user sees
   * (rather than the legacy flat ``availableEvidence`` list, which is unaware
   * of folder navigation and subdocuments).
   */
  getVisibleSelection: () => {
    fileUrls: string[];
    folderUrls: string[];
  };
}

interface NavEntry {
  name: string;
  /**
   * For directories outside any container this is the absolute searchable
   * path. For the container ``file`` entry it's the absolute path of the
   * container. For directories *inside* a container ``path`` is relative
   * to the container so it can be combined with ``fileStatusId`` against
   * ``GET /tree`` (the backend treats path under fileStatusId as relative
   * to the container's internal hierarchy).
   */
  path: string;
  /**
   * Container ``fileStatusId``. Present on the container file entry itself
   * and inherited by every directory / subdocument *inside* that container.
   * ``null`` for plain folders outside any container.
   */
  fileStatusId: string | null;
  nodeType: TreeNodeResponse['nodeType'];
}

/**
 * Build a NavEntry[] for a path-segment click in a search result row.
 *
 * ``displayPath`` is the absolute path shown to the user. When the path
 * crosses a container (``sourceFilePath`` set), segments at or beyond the
 * container's filename produce entries that carry ``containerFsid`` so the
 * resulting ``GET /tree`` query uses ``fileStatusId`` (the only thing the
 * backend can resolve for the contents of a zip/mbox/etc.) instead of a
 * plain ``path`` which wouldn't match anything inside the container.
 */
function buildNavStackForSearchSegment(
  displayPath: string,
  segmentIndex: number,
  sourceFilePath: string | undefined,
  containerFsid: string | null | undefined,
): NavEntry[] {
  const fullSegments = displayPath.replace(/^\/+|\/+$/g, '').split('/').filter(Boolean);
  if (segmentIndex < 0 || segmentIndex >= fullSegments.length) return [];

  const containerSegmentIndex = sourceFilePath
    ? sourceFilePath.replace(/^\/+|\/+$/g, '').split('/').filter(Boolean).length - 1
    : -1;

  const stack: NavEntry[] = [];
  for (let i = 0; i <= segmentIndex; i++) {
    if (containerSegmentIndex < 0 || i < containerSegmentIndex) {
      stack.push({
        name: fullSegments[i]!,
        path: fullSegments.slice(0, i + 1).join('/'),
        fileStatusId: null,
        nodeType: 'directory',
      });
    } else if (i === containerSegmentIndex) {
      stack.push({
        name: fullSegments[i]!,
        path: fullSegments.slice(0, i + 1).join('/'),
        fileStatusId: containerFsid ?? null,
        nodeType: 'file',
      });
    } else {
      stack.push({
        name: fullSegments[i]!,
        path: fullSegments.slice(containerSegmentIndex + 1, i + 1).join('/'),
        fileStatusId: containerFsid ?? null,
        nodeType: 'directory',
      });
    }
  }
  return stack;
}

/** Simple directory-only nav stack from a path (used by flat-folders mode). */
function buildNavStackForFolderPath(path: string): NavEntry[] {
  const normalized = path.replace(/^\/+|\/+$/g, '');
  const segments = normalized.split('/').filter(Boolean);
  return segments.map((name, i) => ({
    name,
    path: segments.slice(0, i + 1).join('/'),
    fileStatusId: null,
    nodeType: 'directory' as const,
  }));
}

const ROW_HEIGHT = 40;
const ROW_GAP = 2;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function nodeFileStatusId(node: TreeNodeResponse): string | null {
  return node.fileStatusId ?? (node.nodeType === 'file' ? (node.id ?? null) : null);
}

/**
 * Build the absolute searchable path for a node. The assistant-service indexes
 * chunks under ``{source.searchablePath}/{relative path inside source}`` (see
 * tai-processing ``TurbopufferDocument._absolute_searchable_path``), so picker
 * URLs that drive the Glob filter must match that shape:
 *
 *   sourceFile.searchablePath = ``MBOX/Manual/evidence_package.mbox``
 *   subdoc.path (relative)      = ``email_1/attachment_2/financial_records.xlsx``
 *   → absolute path             = ``MBOX/Manual/evidence_package.mbox/email_1/attachment_2/financial_records.xlsx``
 *
 * Top-level files / root-level directories already carry an absolute
 * ``node.path`` (= ``location.searchablePath``) so we leave them alone.
 * Subdocs from ``/v1/tree/search`` carry ``sourceFile`` directly. Subdocs and
 * directories returned from the browse path don't (the API only knows their
 * relative position inside the parent file), so callers pass
 * ``contextSourceFilePath`` derived from the breadcrumb's most-recent
 * file-type entry.
 */
function nodeAbsolutePath(
  node: TreeNodeResponse,
  contextSourceFilePath?: string,
): string {
  const sourcePath =
    node.sourceFile?.searchablePath ?? contextSourceFilePath ?? '';
  if (!sourcePath) return node.path;
  return `${sourcePath}/${node.path}`;
}

function nodePickUrl(
  node: TreeNodeResponse,
  toUrl: (path: string) => string,
  contextSourceFilePath?: string,
): string {
  if (node.nodeType === 'subdocument') {
    return toUrl(nodeAbsolutePath(node, contextSourceFilePath));
  }
  return toUrl(node.path);
}

/**
 * URL used for the SourceCard preview popover. Subdocuments carry the actual
 * processed-S3 URI in ``fileInfo.subdocument.fileUrl``; anything else falls
 * back to the scope/pick URL so SourceCard's existing resolver path keeps
 * working.
 */
function nodePreviewUrl(node: TreeNodeResponse, fallbackUrl: string): string {
  if (node.nodeType === 'subdocument') {
    const { fileUrl } = nodeMeta(node);
    if (typeof fileUrl === 'string' && fileUrl.length > 0) return fileUrl;
  }
  return fallbackUrl;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface EvidenceLockerTreeProps {
  /**
   * - ``'tree'`` (default) — hierarchical browse via ``/v1/tree`` + search via
   *   ``/v1/tree/search``. Breadcrumb navigation, drill-in carets, file
   *   previews, subdocument support.
   * - ``'folders'`` — flat list of every folder anywhere in the cell via
   *   ``/v1/folders``. No breadcrumb, no drill-in, no file rows. Used by
   *   the EvidenceLocker's "Folders" filter chip when the user wants to
   *   scope by folder without descending the tree.
   */
  mode?: 'tree' | 'folders';
  /** Debounced search input (only sent upstream when length > 2). */
  searchQuery: string;
  /** Content category tokens, e.g. ``['pdf','image']``. */
  contentCategories?: string[];
  /** Optional case tag → forwarded as ``tags`` filter. */
  caseTag?: string | null;
  /**
   * A specific case is selected but its tag has not resolved. Every query stays
   * off: an untagged request is a whole-tenant listing, not this case's tree.
   */
  caseScopeUnresolved?: boolean;
  /**
   * When ``caseTag`` is set, used to pick which top-level directory is the case
   * root among siblings (name/path/tail match). Usually the selected case id
   * or display name from the parent.
   */
  scopeCaseFolderMatchId?: string | null;
  /**
   * When set with a scoped ``caseTag``, auto-drill into this child folder after
   * the case root directory is opened (e.g. ``Manual`` for Ad Hoc uploads).
   */
  autoBrowseSubfolderName?: string | null;
  /** S3-style URLs currently in ``selectedEvidence`` (pending include). */
  pickedPaths: Set<string>;
  /** S3-style URLs currently in ``excludedEvidence`` (pending exclude). */
  excludedPaths?: Set<string>;
  /** Folder paths currently in ``selectedScopes`` (pending include). */
  pickedFolderPaths?: Set<string>;
  /** Folder paths currently in ``excludedScopes`` (pending exclude). */
  excludedFolderPaths?: Set<string>;
  /**
   * Toggle pick on a file row. Receives the canonicalized lookup path
   * (``toProvenanceLookupPath(node.path)``) that mirrors what the legacy flat
   * list pushed into ``selectedEvidence``.
   */
  onTogglePick: (url: string, node: TreeNodeResponse) => void;
  /**
   * Toggle pick on a directory row. Receives the canonicalized folder path
   * (``toProvenanceLookupPath(node.path)``). Wires to ``selectedScopes`` in
   * the parent so the chat-submit payload sends folder scopes alongside file
   * picks. If not provided, directory rows are not pickable.
   */
  onTogglePickFolder?: (url: string, node: TreeNodeResponse) => void;
  /** Exclude a file row from scope (new stack only; omit to hide the − control). */
  onToggleExclude?: (url: string, node: TreeNodeResponse) => void;
  /** Exclude a folder row from scope (new stack only; omit to hide the − control). */
  onToggleExcludeFolder?: (url: string, node: TreeNodeResponse) => void;
  /**
   * Render the preview-icon + Popover for a file row. The render prop receives
   * the canonical URL so the parent can look up the matching
   * ``availableEvidence`` index, anchor its existing ``Popover``, and reuse
   * the legacy SourceCard preview wiring untouched.
   */
  renderRowPreview?: (params: {
    url: string;
    node: TreeNodeResponse;
    mediaType: string;
    pageCount: number | null;
  }) => React.ReactNode;
  /**
   * Optional content rendered immediately to the right of the filename in each
   * file row (e.g. content-moderation category pills). Receives the same params
   * as ``renderRowPreview`` so the parent can look up per-row metadata.
   */
  renderRowNameSuffix?: (params: {
    url: string;
    node: TreeNodeResponse;
    mediaType: string;
    pageCount: number | null;
  }) => React.ReactNode;
  /**
   * Optional trailing actions for file rows (e.g. delete controls). Rendered in
   * the row action cluster to the left of the preview icon.
   */
  renderRowFileActions?: (params: {
    url: string;
    node: TreeNodeResponse;
    mediaType: string;
    pageCount: number | null;
  }) => React.ReactNode;
  /** Toggle to clear search on the parent (e.g. when navigating into a folder). */
  onClearSearch?: () => void;
  /**
   * Normalize a tree-node ``path`` into the canonical evidence URL. The
   * parent passes the existing ``toProvenanceLookupPath`` helper so the
   * picked URLs match the rest of the question-bar state.
   */
  toUrl: (path: string) => string;
  /**
   * Slot rendered on the right side of the breadcrumb / results-header row.
   * Used by the parent to anchor Select All / Clear All next to the actual
   * view they affect, instead of floating in an unrelated header row above.
   */
  headerActions?: React.ReactNode;
  /**
   * When set, browse to this folder path (a chip segment click). `id` changes
   * on every request so the same path can be opened twice. `container` carries
   * the archive id when the path is inside a zip or mbox.
   */
  browseRequest?: {
    id: number;
    path: string;
    container?: { searchablePath: string; fileStatusId: string } | null;
  } | null;
  ref?: React.Ref<EvidenceLockerTreeHandle>;
}

/**
 * Light-themed tree picker for the EvidenceLocker. Drops in where the legacy
 * flat ``availableEvidence`` list rendered, but exposes the same pick
 * semantics so ``selectedEvidence``/``scopesAndFiles.files`` keep flowing
 * unchanged.
 *
 * Modes:
 * - **Browse**: ``searchQuery.length <= 2`` AND no ``contentCategories`` → uses
 *   ``GET /tree`` with breadcrumb-driven path navigation and infinite scroll.
 * - **Search**: 3+ char query OR any category selected → uses
 *   ``GET /tree/search`` (flat results across the cell).
 *
 * Subdocument nodes are filtered out in v1 to keep the picked URL contract
 * identical to the flat list (one URL per ingested source file). They can
 * be re-introduced once subdocument-level chat scoping ships.
 */
export default function EvidenceLockerTreeNew({
  mode = 'tree',
  searchQuery,
  contentCategories,
  caseTag,
  caseScopeUnresolved = false,
  scopeCaseFolderMatchId,
  autoBrowseSubfolderName,
  pickedPaths,
  excludedPaths,
  pickedFolderPaths,
  excludedFolderPaths,
  onTogglePick,
  onTogglePickFolder,
  onToggleExclude,
  onToggleExcludeFolder,
  renderRowPreview,
  renderRowNameSuffix,
  renderRowFileActions,
  onClearSearch,
  toUrl,
  headerActions,
  browseRequest,
  ref,
}: EvidenceLockerTreeProps) {
  const t = useTimePilotT();
  const inverse = useEvidenceLockerAppearance() === 'inverse';
  const tone = lockerTreeClasses(inverse);
  const buttonBackground = inverse ? 'inverse' : 'default';
  const mutedButtonColor = inverse ? 'light' : 'subdued';
  const [navStack, setNavStack] = useState<NavEntry[]>([]);
  /** Number of leading ``navStack`` entries hidden in the breadcrumb (the case folder treated as root). */
  const [caseBrowseRootCrumbDepth, setCaseBrowseRootCrumbDepth] = useState(0);
  /**
   * When set, folders mode leaves the flat ``/folders`` list and browses that
   * path via ``/tree`` (segment click or row drill).
   */
  const [foldersDrillPath, setFoldersDrillPath] = useState<string | null>(null);
  const isFoldersMode = mode === 'folders';
  const isFoldersFlatView = isFoldersMode && foldersDrillPath === null;
  const isFoldersDrillBrowse = isFoldersMode && foldersDrillPath !== null;

  useEffect(() => {
    if (!isFoldersMode) {
      setFoldersDrillPath(null);
    }
  }, [isFoldersMode]);

  // Flat folders list: no nav stack. Drilling into a path uses tree browse.
  useEffect(() => {
    if (!isFoldersFlatView) return;
    setNavStack([]);
    setCaseBrowseRootCrumbDepth(0);
  }, [isFoldersFlatView]);

  useEffect(() => {
    setNavStack([]);
    setCaseBrowseRootCrumbDepth(0);
    setFoldersDrillPath(null);
  }, [caseTag]);

  const hasTextSearch = !!(searchQuery && searchQuery.trim().length > 2);
  const hasCategoryFilter = !!(contentCategories && contentCategories.length > 0);
  const currentEntry = navStack[navStack.length - 1];
  // ``/tree/search`` cannot take ``fileStatusId``. Category-filtered search hits
  // for archives (zips tagged ``image``/etc.) must switch to browse when the user
  // drills into the container — otherwise the same flat search payload is
  // re-shown and the breadcrumb appends the zip name forever.
  const insideContainer = !!currentEntry?.fileStatusId;
  const isSearching =
    !isFoldersMode && (hasTextSearch || (hasCategoryFilter && !insideContainer));
  /** Compact path row for text search (no category-only filter browse). */
  const showTextSearchResultRow = isSearching && hasTextSearch && !hasCategoryFilter;
  // Filetype chips search without a text query. Those rows still need the
  // condensed path so files from different folders stay distinguishable.
  const showSearchResultPath = isSearching;

  /** Case-folder path prefix hidden in search result rows when a case is scoped. */
  const searchPathCasePrefix = useMemo((): string | undefined => {
    if (!caseTag) return undefined;
    if (caseBrowseRootCrumbDepth > 0 && navStack[caseBrowseRootCrumbDepth - 1]) {
      return navStack[caseBrowseRootCrumbDepth - 1].path;
    }
    if (navStack.length > 0) return navStack[0].path;
    const id = scopeCaseFolderMatchId?.trim();
    if (id) return id;
    return caseTag.trim();
  }, [caseTag, navStack, caseBrowseRootCrumbDepth, scopeCaseFolderMatchId]);

  // The most-recent ``file``-type breadcrumb entry's ``path`` is the absolute
  // ``searchable_path`` of the source file the user drilled into (e.g.
  // ``MBOX/Manual/evidence_package.mbox``). Subdocuments and virtual
  // directories returned by the browse path are reported relative to that
  // file, so we need this prefix to reconstruct their absolute searchable
  // path for picker URLs. ``undefined`` when not inside any file.
  const contextSourceFilePath = useMemo(() => {
    for (let i = navStack.length - 1; i >= 0; i--) {
      if (navStack[i].nodeType === 'file') return navStack[i].path;
    }
    return undefined;
  }, [navStack]);
  const tags = caseTag ? [caseTag] : undefined;
  const tagMatch = caseTag ? ('any' as const) : undefined;

  // ``path`` + ``fileStatusId`` interplay (mirrors zprovenance NodeStatusTreeView):
  //   - No nav entry                       → list root  (no path, no fsid)
  //   - Directory entry, no fsid           → list path  (path only)
  //   - File entry (fsid set)              → list file's direct children (fsid only)
  //   - Anything-else-inside-a-file (fsid + nodeType !== 'file')
  //                                         → BOTH fsid AND path so the backend
  //                                           scopes within that file. This covers
  //                                           both subdocuments AND virtual
  //                                           directories that exist inside a file
  //                                           (e.g. mbox folders).
  const treeParams = currentEntry
    ? currentEntry.fileStatusId
      ? {
          fileStatusId: currentEntry.fileStatusId,
          path: currentEntry.nodeType === 'file' ? undefined : currentEntry.path,
        }
      : { path: currentEntry.path, fileStatusId: undefined }
    : { path: undefined, fileStatusId: undefined };

  const treeQuery = useProvenanceTree({
    path: treeParams.path,
    fileStatusId: treeParams.fileStatusId,
    view: 'user',
    tags,
    tagMatch,
    // Keep Image/Audio/… filters active while browsing inside a container.
    contentCategories: insideContainer ? contentCategories : undefined,
    enabled:
      !caseScopeUnresolved && !isSearching && (!isFoldersMode || isFoldersDrillBrowse),
  });

  // Folders mode: ``GET /v1/folders`` returns every directory anywhere in
  // the cell, with optional ``searchQuery`` substring filter. We synthesize
  // ``TreeNodeResponse`` directories from the API payload so the existing
  // ``TreeRow`` rendering path (pick checkbox, hover styling, virtualization,
  // ``getVisibleSelection``) stays untouched. ``hasChildren`` is forced to
  // ``false`` so no drill-in caret renders — folders mode is intentionally
  // flat.
  const foldersSearch =
    searchQuery && searchQuery.trim().length > 2 ? searchQuery.trim() : undefined;
  const foldersQuery = useProvenanceFolders({
    searchQuery: foldersSearch,
    tags,
    tagMatch,
    enabled: !caseScopeUnresolved && isFoldersFlatView,
  });
  // ``enabled: false`` stops fetching but not reading: with no ``caseTag`` these
  // hooks share the untagged All Cases key, so a previously cached cell-wide
  // listing would still render. Read nothing at all until the case resolves.
  const folderItems: FolderItem[] = useMemo(
    () =>
      caseScopeUnresolved
        ? []
        : (foldersQuery.data?.pages.flatMap((p) => p.items) ?? []),
    [foldersQuery.data, caseScopeUnresolved],
  );
  const folderNodes = useMemo<TreeNodeResponse[]>(
    () =>
      // Render the *full* path (e.g. "Enron/Manual/E-Mails") as the row
      // label since folders mode is flat — without ancestors, a row labelled
      // just "E-Mails" would be ambiguous between every folder of that name
      // in the cell. ``path`` stays the canonical pick URL.
      folderItems.map((f) => ({
        nodeType: 'directory' as const,
        name: f.path,
        path: f.path,
        hasChildren: false,
      })),
    [folderItems],
  );

  // ``q`` selection for search mode:
  //   - User-typed query wins.
  //   - Otherwise fall back to the current nav-stack folder path so a
  //     content-category filter (Audio / Video / …) combined with breadcrumb
  //     drill-in stays scoped to the folder the user clicked into.
  //
  // The backend's ``/tree/search`` uses ILIKE substring match on
  // ``searchable_path``, so passing the folder path as ``q`` effectively
  // restricts category-filtered results to that subtree.
  //
  // Only do the path-fallback for plain directory entries — drilling into a
  // file or subdocument relies on ``fileStatusId`` (which ``/tree/search``
  // doesn't accept) so we skip the search in those cases.
  const userQuery = searchQuery && searchQuery.trim().length > 2 ? searchQuery.trim() : undefined;
  const navPathQuery =
    !userQuery && currentEntry && !currentEntry.fileStatusId ? currentEntry.path : undefined;
  const searchTerm = userQuery ?? navPathQuery;

  const searchQueryHook = useProvenanceTreeSearch({
    q: searchTerm,
    contentCategories,
    view: 'user',
    tags,
    tagMatch,
    enabled: !caseScopeUnresolved && isSearching,
  });

  const browseItems = useMemo<TreeNodeResponse[]>(
    () =>
      caseScopeUnresolved
        ? []
        : (treeQuery.data?.pages.flatMap((p) => p.items) ?? []),
    [treeQuery.data, caseScopeUnresolved],
  );
  const searchItems = caseScopeUnresolved ? [] : (searchQueryHook.data ?? []);

  // With a case tag, the tree root is often a single case folder — drill in once
  // so the locker lists that folder's children and the breadcrumb stays at "Root".
  useEffect(() => {
    if (!caseTag || isFoldersMode || isSearching) return;
    if (navStack.length > 0) return;
    if (treeQuery.isLoading || browseItems.length === 0) return;
    const caseDir = pickCaseRootDirectoryNode(browseItems, caseTag, scopeCaseFolderMatchId);
    if (!caseDir) return;
    setNavStack([
      { name: caseDir.name, path: caseDir.path, fileStatusId: null, nodeType: 'directory' },
    ]);
    setCaseBrowseRootCrumbDepth(1);
  }, [
    caseTag,
    scopeCaseFolderMatchId,
    isFoldersMode,
    isSearching,
    browseItems,
    treeQuery.isLoading,
    navStack.length,
  ]);

  // After the case root opens, optionally drill one level deeper (Ad Hoc → Manual).
  useEffect(() => {
    const subfolder = autoBrowseSubfolderName?.trim();
    if (!subfolder || !caseTag || isFoldersMode || isSearching) return;
    if (navStack.length !== 1 || caseBrowseRootCrumbDepth !== 1) return;
    if (treeQuery.isLoading || browseItems.length === 0) return;
    const childDir = browseItems.find(
      (n) =>
        n.nodeType === 'directory' &&
        (n.name === subfolder || pathTail(n.path) === subfolder),
    );
    if (!childDir) return;
    setNavStack((prev) => [
      ...prev,
      {
        name: childDir.name,
        path: childDir.path,
        fileStatusId: null,
        nodeType: 'directory',
      },
    ]);
  }, [
    autoBrowseSubfolderName,
    caseTag,
    isFoldersMode,
    isSearching,
    browseItems,
    treeQuery.isLoading,
    navStack.length,
    caseBrowseRootCrumbDepth,
  ]);

  // Folders-only mode: jump straight to ``{case}/Manual`` when Ad Hoc is scoped.
  useEffect(() => {
    if (!isFoldersFlatView) return;
    const subfolder = autoBrowseSubfolderName?.trim();
    const matchId = scopeCaseFolderMatchId?.trim();
    if (!subfolder || !matchId) return;
    const drillPath = `${matchId}/${subfolder}`.replace(/^\/+|\/+$/g, '');
    setFoldersDrillPath(drillPath);
    setNavStack(buildNavStackForFolderPath(drillPath));
  }, [isFoldersFlatView, autoBrowseSubfolderName, scopeCaseFolderMatchId, caseTag]);

  // Show all node kinds. Files / subdocuments with ``hasChildren`` get a
  // drill-in affordance so users can navigate into multi-document containers
  // (mbox files, archives, PDFs with subdocuments, …).
  const displayItems = useMemo(() => {
    if (isFoldersFlatView) return folderNodes;
    if (isFoldersDrillBrowse) return browseItems;
    return isSearching ? searchItems : browseItems;
  }, [
    isFoldersFlatView,
    isFoldersDrillBrowse,
    folderNodes,
    isSearching,
    searchItems,
    browseItems,
  ]);

  const browseTotal = caseScopeUnresolved ? 0 : (treeQuery.data?.pages?.[0]?.total ?? 0);
  // ``fetchNextPage`` is imperative and ignores ``enabled``, so an untagged
  // "load more" has to be unreachable rather than merely disabled.
  const browseHasMore = !caseScopeUnresolved && treeQuery.hasNextPage === true;
  const browseFetchingMore = treeQuery.isFetchingNextPage;
  const fetchNextTreePage = treeQuery.fetchNextPage;
  const foldersTotal = caseScopeUnresolved
    ? 0
    : (foldersQuery.data?.pages?.[0]?.total ?? folderNodes.length);
  const foldersHasMore = !caseScopeUnresolved && foldersQuery.hasNextPage === true;
  const foldersFetchingMore = foldersQuery.isFetchingNextPage;
  const fetchNextFoldersPage = foldersQuery.fetchNextPage;

  // Unify the paging surface so the virtualizer/sentinel logic below stays
  // mode-agnostic. ``loadMore`` is the only handle the JSX below needs.
  const paginatorHasMore = isFoldersFlatView
    ? foldersHasMore
    : !isSearching && browseHasMore;
  const paginatorFetchingMore = isFoldersFlatView
    ? foldersFetchingMore
    : !isSearching && browseFetchingMore;
  const loadMore = isFoldersFlatView ? fetchNextFoldersPage : fetchNextTreePage;

  const loading = isFoldersFlatView
    ? foldersQuery.isLoading
    : isSearching
      ? searchQueryHook.isLoading
      : treeQuery.isLoading;
  const error = isFoldersFlatView
    ? foldersQuery.error
    : isSearching
      ? searchQueryHook.error
      : treeQuery.error;
  const showEmpty = !loading && !error && displayItems.length === 0;

  const showLoadMore = paginatorHasMore && !loading;
  const showLoadingMore = paginatorFetchingMore && displayItems.length > 0;
  const hasExtraRow = showLoadMore || showLoadingMore;

  const scrollRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const virtualizer = useVirtualizer({
    count: displayItems.length + (hasExtraRow ? 1 : 0),
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT,
    gap: ROW_GAP,
    overscan: 10,
  });

  // Infinite-scroll sentinel — covers both tree-browse and folders-mode
  // pagination. Search responses come back as a single flat array so they
  // intentionally skip this.
  useEffect(() => {
    const root = scrollRef.current;
    const target = sentinelRef.current;
    if (!root || !target) return;
    if (!paginatorHasMore) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && !paginatorFetchingMore) {
            loadMore();
          }
        }
      },
      { root, rootMargin: '100px', threshold: 0 },
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [paginatorHasMore, paginatorFetchingMore, loadMore]);

  const navigateToFolderPath = useCallback(
    (path: string) => {
      const normalized = path.replace(/^\/+|\/+$/g, '');
      if (!normalized) {
        setNavStack([]);
        setFoldersDrillPath(null);
        onClearSearch?.();
        return;
      }
      if (isFoldersMode) {
        setFoldersDrillPath(normalized);
      }
      setNavStack(buildNavStackForFolderPath(normalized));
      onClearSearch?.();
    },
    [isFoldersMode, onClearSearch],
  );

  /**
   * Set the nav stack directly (e.g. from a search-result path-segment click
   * which already built the stack with container fileStatusId metadata).
   */
  const navigateToStack = useCallback(
    (stack: NavEntry[]) => {
      if (stack.length === 0) {
        setNavStack([]);
        setFoldersDrillPath(null);
        onClearSearch?.();
        return;
      }
      setNavStack(stack);
      onClearSearch?.();
    },
    [onClearSearch],
  );

  const seenBrowseId = useRef(0);
  useEffect(() => {
    if (!browseRequest || browseRequest.id === seenBrowseId.current) return;
    seenBrowseId.current = browseRequest.id;
    const container = browseRequest.container;
    const normalized = browseRequest.path.replace(/^\/+|\/+$/g, '');
    if (container?.fileStatusId && container.searchablePath && normalized) {
      const segments = normalized.split('/').filter(Boolean);
      if (isFoldersMode) setFoldersDrillPath(normalized);
      navigateToStack(
        buildNavStackForSearchSegment(
          normalized,
          segments.length - 1,
          container.searchablePath,
          container.fileStatusId,
        ),
      );
      return;
    }
    navigateToFolderPath(browseRequest.path);
  }, [browseRequest, isFoldersMode, navigateToFolderPath, navigateToStack]);

  const navigateInto = useCallback(
    (node: TreeNodeResponse) => {
      setNavStack((prev) => {
        const parent = prev[prev.length - 1];
        let fsId: string | null = null;
        if (node.nodeType === 'file') {
          fsId = nodeFileStatusId(node);
        } else if (node.fileStatusId) {
          fsId = node.fileStatusId;
        } else if (parent?.fileStatusId) {
          fsId = parent.fileStatusId;
        }
        const next = [
          ...prev,
          { name: node.name, path: node.path, fileStatusId: fsId, nodeType: node.nodeType },
        ];
        if (isFoldersDrillBrowse && node.nodeType === 'directory') {
          setFoldersDrillPath(node.path);
        }
        return next;
      });
    },
    [isFoldersDrillBrowse],
  );

  const navigateTo = useCallback(
    (index: number) => {
      if (index < 0) {
        setNavStack([]);
        setFoldersDrillPath(null);
        return;
      }
      setNavStack((prev) => {
        const next = prev.slice(0, index + 1);
        const entry = next[next.length - 1];
        if (isFoldersDrillBrowse && entry?.nodeType === 'directory') {
          setFoldersDrillPath(entry.path);
        }
        return next;
      });
    },
    [isFoldersDrillBrowse],
  );

  const handleRowClick = useCallback(
    (node: TreeNodeResponse) => {
      if (isFoldersFlatView) {
        if (node.nodeType === 'directory') {
          navigateToFolderPath(node.path);
        }
        return;
      }
      const isDirectory = node.nodeType === 'directory';
      const canDrill = isDirectory || node.hasChildren === true;
      if (!canDrill) return;
      if (isSearching && onClearSearch) onClearSearch();
      navigateInto(node);
    },
    [
      isFoldersFlatView,
      isSearching,
      navigateInto,
      navigateToFolderPath,
      onClearSearch,
    ],
  );

  const handleNavigateToNode = useCallback(
    (node: TreeNodeResponse) => {
      // For nodes that live inside a container (subdocuments + virtual dirs
      // returned by ``/tree/search``), navigate via ``buildNavStackForSearchSegment``
      // so each segment past the container filename carries the container's
      // fileStatusId. Without this the resulting ``GET /tree`` calls would use
      // an absolute path that doesn't exist in the database.
      //
      // ``searchDisplayPath`` (not ``nodeAbsolutePath``) is the source of
      // truth here: it guarantees ``node.name`` is the trailing segment,
      // which the breadcrumb already relies on. ``nodeAbsolutePath`` only
      // concatenates ``sourceFile.searchablePath + node.path`` and ``node.path``
      // for virtual directories / some subdocuments may not end in
      // ``node.name`` — that mismatch would make ``segs.length - 2`` drop the
      // wrong segment and land the user a level too high (or at locker root).
      if (node.sourceFile) {
        const displayPath = searchDisplayPath(node, undefined);
        const segs = displayPath.replace(/^\/+|\/+$/g, '').split('/').filter(Boolean);
        const containerFsid = node.sourceFile.id ?? node.fileStatusId ?? null;
        // Drop the leaf (the node itself); land *inside* the parent dir / file
        // so the user can see siblings.
        const targetIndex = segs.length - 2;
        if (targetIndex < 0) {
          setNavStack([]);
        } else {
          setNavStack(
            buildNavStackForSearchSegment(
              displayPath,
              targetIndex,
              node.sourceFile.searchablePath,
              containerFsid,
            ),
          );
        }
      } else if (node.nodeType === 'directory') {
        setNavStack(buildNavStackForFolderPath(node.path));
      } else {
        const segments = node.path.split('/').filter(Boolean);
        segments.pop();
        setNavStack(buildNavStackForFolderPath(segments.join('/')));
      }
      onClearSearch?.();
    },
    [onClearSearch],
  );

  const getVisibleSelection = useCallback(() => {
    const fileUrls: string[] = [];
    const folderUrls: string[] = [];
    for (const node of displayItems) {
      if (node.nodeType === 'directory') {
        folderUrls.push(nodeAbsolutePath(node, contextSourceFilePath));
      } else {
        fileUrls.push(nodePickUrl(node, toUrl, contextSourceFilePath));
      }
    }
    return { fileUrls, folderUrls };
  }, [displayItems, toUrl, contextSourceFilePath]);

  useImperativeHandle(
    ref,
    () => ({ navigateToNode: handleNavigateToNode, getVisibleSelection }),
    [handleNavigateToNode, getVisibleSelection],
  );

  // Show the breadcrumb whenever there's a nav stack, even in search mode —
  // otherwise drilling into a folder under a category filter would strand the
  // user with no way to navigate further (path-links only exist *on rows*).
  // Folders mode has its own dedicated header (count + action buttons).
  const showBreadcrumb =
    isFoldersDrillBrowse || (!isFoldersMode && (!isSearching || navStack.length > 0));
  const showResultsCountRow =
    !isFoldersMode && !showEmpty && (displayItems.length > 0 || isSearching);
  const resultsCount = isSearching
    ? displayItems.length
    : browseTotal > 0
      ? browseTotal
      : displayItems.length;

  return (
    <div className="flex min-w-0 w-full flex-1 flex-col min-h-0 overflow-hidden">
      {isFoldersFlatView && (
        <div className={`flex items-center gap-2 border-b px-3 pt-2 pb-1 text-[14px] ${tone.foldersBar}`}>
          <span className="truncate">
            {loading
              ? t('chat.evidence.tree.loadingFolders')
              : t(foldersTotal === 1
                ? 'chat.evidence.tree.folderCount'
                : 'chat.evidence.tree.folderCountPlural', {
                  count: folderNodes.length,
                  total: foldersTotal > folderNodes.length ? ` ${t('chat.evidence.tree.ofTotal', { total: foldersTotal })}` : '',
                  matching: foldersSearch
                    ? ` ${t('chat.evidence.tree.matching', { search: foldersSearch })}`
                    : '',
                })}
          </span>
        </div>
      )}
      {showBreadcrumb && (
        <div className="flex items-center gap-2">
          <div className="min-w-0 flex-1">
            <Breadcrumb
              navStack={navStack}
              breadcrumbStartIndex={caseBrowseRootCrumbDepth}
              onNavigate={navigateTo}
              onHome={() =>
                isFoldersDrillBrowse
                  ? navigateTo(-1)
                  : navigateTo(caseBrowseRootCrumbDepth > 0 ? caseBrowseRootCrumbDepth - 1 : -1)
              }
              totalCount={browseTotal}
            />
          </div>
        </div>
      )}
      {showResultsCountRow && (
        <div className={`flex items-center justify-between gap-2 pl-4 pr-6 py-3 text-sm ${tone.fg}`}>
          <span className="truncate font-medium">
            {searchQueryHook.isLoading
              ? t('chat.evidence.tree.searching')
              : t(resultsCount === 1
                ? 'chat.evidence.tree.resultCount'
                : 'chat.evidence.tree.resultCountPlural', { count: resultsCount })}
          </span>
          <div className="flex flex-shrink-0 items-center gap-3">
            {navPathQuery && isSearching ? (
              <Button
                type="button"
                size="sm"
                style="text"
                color={mutedButtonColor}
                background={buttonBackground}
                onClick={() => navigateTo(-1)}
                className={tone.muted}
                title={t('chat.evidence.tree.searchAllHelp')}
              >
                {t('chat.evidence.tree.searchAll')}
              </Button>
            ) : null}
            {headerActions}
          </div>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col min-h-0 py-1">
        {loading && displayItems.length === 0 ? (
          <div className="flex items-center justify-center py-6">
            <div className={`animate-pulse text-sm ${tone.muted70}`}>{t('chat.evidence.tree.loading')}</div>
          </div>
        ) : error ? (
          <div className="px-3 py-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md">
            {error.message}
          </div>
        ) : showEmpty ? (
          <div className={`text-sm text-center py-6 ${tone.muted}`}>
            {isFoldersFlatView
              ? foldersSearch
                ? t('chat.evidence.tree.noFoldersMatch')
                : t('chat.evidence.tree.noFolders')
              : isFoldersDrillBrowse
                ? t('chat.evidence.tree.emptyFolder')
              : isSearching
                ? t('chat.evidence.tree.noResults')
                : t('chat.evidence.tree.emptyFolder')}
          </div>
        ) : (
          <div
            ref={scrollRef}
            className="evidence-locker-scroll h-full min-h-0 w-full pr-1 [scrollbar-gutter:stable]"
          >
            <div
              style={{
                height: virtualizer.getTotalSize(),
                width: '100%',
                position: 'relative',
              }}
            >
              {virtualizer.getVirtualItems().map((vi) => {
                const isExtra = vi.index >= displayItems.length;
                return (
                  <div
                    key={vi.key}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: vi.size,
                      transform: `translateY(${vi.start}px)`,
                    }}
                  >
                    {isExtra ? (
                      <div className="flex items-center justify-center gap-2 h-full px-3">
                        {showLoadingMore ? (
                          <div className={`animate-pulse text-xs ${tone.muted}`}>{t('chat.evidence.tree.loadingMore')}</div>
                        ) : (
                          <Button
                            type="button"
                            size="sm"
                            style="outline"
                            color={mutedButtonColor}
                            background={buttonBackground}
                            onClick={() => loadMore()}
                            className="px-3 py-1 text-xs"
                          >
                            {t('chat.evidence.tree.loadMore')}
                          </Button>
                        )}
                      </div>
                    ) : (
                      <TreeRow
                        node={displayItems[vi.index]}
                        toUrl={toUrl}
                        contextSourceFilePath={contextSourceFilePath}
                        pickedPaths={pickedPaths}
                        excludedPaths={excludedPaths}
                        pickedFolderPaths={pickedFolderPaths}
                        excludedFolderPaths={excludedFolderPaths}
                        isSearchResult={showTextSearchResultRow}
                        showSearchResultPath={showSearchResultPath}
                        showFolderPathSegments={isFoldersFlatView}
                        onFolderPathSegmentClick={navigateToFolderPath}
                        onSearchSegmentNavigate={navigateToStack}
                        onRowClick={handleRowClick}
                        onTogglePick={onTogglePick}
                        onTogglePickFolder={onTogglePickFolder}
                        onToggleExclude={onToggleExclude}
                        onToggleExcludeFolder={onToggleExcludeFolder}
                        onNavigateToParent={handleNavigateToNode}
                        searchPathCasePrefix={
                          showSearchResultPath || showTextSearchResultRow
                            ? searchPathCasePrefix
                            : undefined
                        }
                        renderRowPreview={renderRowPreview}
                        renderRowNameSuffix={renderRowNameSuffix}
                        renderRowFileActions={renderRowFileActions}
                      />
                    )}
                  </div>
                );
              })}
            </div>
            <div ref={sentinelRef} aria-hidden="true" className="h-px w-full" />
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Breadcrumb
// ---------------------------------------------------------------------------

function Breadcrumb({
  navStack,
  breadcrumbStartIndex = 0,
  onNavigate,
  onHome,
  totalCount,
}: {
  navStack: NavEntry[];
  breadcrumbStartIndex?: number;
  onNavigate: (index: number) => void;
  onHome: () => void;
  totalCount: number;
}) {
  const t = useTimePilotT();
  const toneName = useEvidenceLockerAppearance() === 'inverse' ? 'dark' : 'light';
  const tone = lockerTreeClasses(toneName === 'dark');
  const caseLabels = useCaseFolderLabels();
  const start = Math.min(Math.max(0, breadcrumbStartIndex), navStack.length);
  const items = navStackToBreadcrumbItems(navStack, caseLabels, start).map((item, index) => {
    const entry = navStack[start + index];
    const isFile = entry?.nodeType === 'file' || entry?.nodeType === 'subdocument';
    return {
      ...item,
      icon: crumbIcon(isFile && entry ? iconForFileName(entry.name) : FolderOpenIcon),
    };
  });
  const atHome = navStack.length <= start;
  return (
    <div className="min-w-0 py-4 pl-4 pr-2">
      <Breadcrumbs
        className="evidence-locker-breadcrumbs"
        tone={toneName}
        aria-label={t('chat.evidence.tree.root')}
        root={{
          id: 'locker',
          label: t('chat.evidence.tree.locker'),
          icon: <DresserIcon size={16} weight="bold" />,
        }}
        items={items}
        currentId={atHome ? 'locker' : items[items.length - 1]?.id}
        onNavigate={(_item, index) => {
          if (index < 0) onHome();
          else onNavigate(start + index);
        }}
        trailing={
          totalCount > 0 && navStack.length === 0 ? (
            <span className={`text-sm font-semibold ${tone.muted50}`}>
              {t(totalCount === 1
                ? 'chat.evidence.tree.itemCount'
                : 'chat.evidence.tree.itemCountPlural', { count: totalCount })}
            </span>
          ) : null
        }
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// FolderPathSegments — clickable path parts in flat folders list
// ---------------------------------------------------------------------------

function crumbIcon(Icon: React.ElementType) {
  return <Icon size={16} weight="regular" aria-hidden />;
}

/** Folder icon on directory segments; `leafIcon` marks a file at the end of the path. */
function withCrumbIcons(items: BreadcrumbItem[], leafIcon?: React.ElementType): BreadcrumbItem[] {
  return items.map((item, index) => ({
    ...item,
    icon: crumbIcon(index === items.length - 1 && leafIcon ? leafIcon : FolderOpenIcon),
  }));
}

function FolderPathSegments({
  path,
  onSegmentClick,
  showLockerRoot = false,
  omitDisplayPrefix,
  leafSuffix,
  leafNode,
}: {
  path: string;
  onSegmentClick: (prefixPath: string, segmentIndex: number) => void;
  showLockerRoot?: boolean;
  omitDisplayPrefix?: string | null;
  leafSuffix?: React.ReactNode;
  /** When set, the last segment uses this node's file icon. Otherwise every segment is a folder. */
  leafNode?: TreeNodeResponse | null;
}) {
  const t = useTimePilotT();
  const toneName = useEvidenceLockerAppearance() === 'inverse' ? 'dark' : 'light';
  const caseLabels = useCaseFolderLabels();
  const { items: pathItems, omitCount } = pathToBreadcrumbItems(path, omitDisplayPrefix, caseLabels);
  const leafIcon =
    leafNode && leafNode.nodeType !== 'directory' ? iconForFileNode(leafNode) : undefined;
  const items = withCrumbIcons(pathItems, leafIcon);
  if (items.length === 0 && !showLockerRoot) return null;
  return (
    // The row is also a click target. Keep crumb clicks (and Enter/Space on a
    // focused crumb) from bubbling into that handler, which would open the
    // leaf instead of the folder that was chosen.
    <div
      className="min-w-0 flex-1"
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') event.stopPropagation();
      }}
    >
      <Breadcrumbs
        className="evidence-locker-breadcrumbs min-w-0 w-full"
        tone={toneName}
        aria-label={t('chat.evidence.tree.root')}
        root={
          showLockerRoot
            ? {
                id: 'locker',
                label: t('chat.evidence.tree.locker'),
                icon: <DresserIcon size={16} weight="bold" />,
              }
            : undefined
        }
        items={items}
        onNavigate={(item, index) => {
          if (index < 0) onSegmentClick('', -1);
          else onSegmentClick(item.id, omitCount + index);
        }}
        leafSuffix={leafSuffix}
      />
    </div>
  );
}

function evidencePathSegments(path: string, leafName: string): string[] {
  const normalized = path.replace(/^\/+|\/+$/g, '');
  const segments = normalized.split('/').filter(Boolean);
  if (segments.length === 0) return [leafName];
  if (segments[segments.length - 1] !== leafName) {
    return [...segments, leafName];
  }
  return segments;
}

/** Full searchable path string for a search row (used for segment navigation). */
function searchDisplayPath(
  node: TreeNodeResponse,
  contextSourceFilePath?: string,
): string {
  const absolute = nodeAbsolutePath(node, contextSourceFilePath);
  return evidencePathSegments(absolute, node.name).join('/');
}

/**
 * Clickable path segments for search result rows (compact: no Locker root).
 *
 * When the row's node lives inside a container file the click handler must
 * navigate using the container's ``fileStatusId`` for any segment past the
 * container's filename — querying by ``path`` alone returns nothing for the
 * synthetic hierarchy inside zip / mbox / etc.
 */
function EvidenceResultPath({
  displayPath,
  node,
  contextSourceFilePath,
  onSegmentNavigate,
  omitCasePrefix,
  leafSuffix,
}: {
  displayPath: string;
  node: TreeNodeResponse;
  contextSourceFilePath?: string;
  onSegmentNavigate: (stack: NavEntry[]) => void;
  omitCasePrefix?: string | null;
  leafSuffix?: React.ReactNode;
}) {
  const sourceFilePath =
    node.sourceFile?.searchablePath ?? contextSourceFilePath;
  const containerFsid =
    node.sourceFile?.id ??
    (node.nodeType === 'subdocument' ? node.fileStatusId ?? null : null);

  const handleClick = (_prefixPath: string, segmentIndex: number) => {
    if (segmentIndex < 0) {
      onSegmentNavigate([]);
      return;
    }
    onSegmentNavigate(
      buildNavStackForSearchSegment(
        displayPath,
        segmentIndex,
        sourceFilePath,
        containerFsid,
      ),
    );
  };

  return (
    <FolderPathSegments
      path={displayPath}
      onSegmentClick={handleClick}
      omitDisplayPrefix={omitCasePrefix}
      leafSuffix={leafSuffix}
      leafNode={node}
    />
  );
}

// ---------------------------------------------------------------------------
// ScopeCycleButton — plus → check (include) → prohibit (exclude) → plus
// ---------------------------------------------------------------------------

function ScopeCycleButton({
  isPicked,
  isExcluded,
  onCycle,
  itemLabel,
}: {
  isPicked: boolean;
  isExcluded: boolean;
  onCycle: () => void;
  itemLabel: string;
}) {
  const t = useTimePilotT();
  const inverse = useEvidenceLockerAppearance() === 'inverse';
  const ariaLabel = isPicked
    ? t('chat.evidence.tree.excludeFromScope', { item: itemLabel })
    : isExcluded
      ? t('chat.evidence.tree.clearScope', { item: itemLabel })
      : t('chat.evidence.tree.includeInScope', { item: itemLabel });

  return (
    <Button
      type="button"
      iconOnly
      size="sm"
      style="text"
      color={inverse ? 'light' : 'subdued'}
      background={inverse ? 'inverse' : 'default'}
      onClick={(e) => {
        e.stopPropagation();
        onCycle();
      }}
      aria-label={ariaLabel}
      title={ariaLabel}
      className="h-7 w-7 shrink-0"
    >
      {isPicked ? (
        <CheckIcon weight="bold" className="text-green-600" />
      ) : isExcluded ? (
        <ProhibitIcon weight="bold" className="text-red-600" />
      ) : (
        <PlusIcon weight="bold" />
      )}
    </Button>
  );
}

// ---------------------------------------------------------------------------
// TreeRow
// ---------------------------------------------------------------------------

interface TreeRowProps {
  node: TreeNodeResponse;
  toUrl: (path: string) => string;
  contextSourceFilePath?: string;
  pickedPaths: Set<string>;
  excludedPaths?: Set<string>;
  pickedFolderPaths?: Set<string>;
  excludedFolderPaths?: Set<string>;
  isSearchResult: boolean;
  /** Condensed breadcrumb path (text search and filetype filters). */
  showSearchResultPath?: boolean;
  showFolderPathSegments?: boolean;
  /**
   * Flat-folders mode segment click — receives the prefix path (no container
   * concerns; flat-folders never crosses container boundaries).
   */
  onFolderPathSegmentClick?: (prefixPath: string) => void;
  /**
   * Search-result row segment click — receives a pre-built nav stack that
   * accounts for container ``fileStatusId`` boundaries. Used in place of the
   * flat-folders callback when the row is rendered in search mode.
   */
  onSearchSegmentNavigate?: (stack: NavEntry[]) => void;
  onRowClick: (node: TreeNodeResponse) => void;
  onTogglePick: (url: string, node: TreeNodeResponse) => void;
  onTogglePickFolder?: (url: string, node: TreeNodeResponse) => void;
  onToggleExclude?: (url: string, node: TreeNodeResponse) => void;
  onToggleExcludeFolder?: (url: string, node: TreeNodeResponse) => void;
  onNavigateToParent: (node: TreeNodeResponse) => void;
  /** Case-folder prefix omitted from search path display when scoped to a case. */
  searchPathCasePrefix?: string | null;
  renderRowPreview?: EvidenceLockerTreeProps['renderRowPreview'];
  renderRowNameSuffix?: EvidenceLockerTreeProps['renderRowNameSuffix'];
  renderRowFileActions?: EvidenceLockerTreeProps['renderRowFileActions'];
}

function TreeRow({
  node,
  toUrl,
  contextSourceFilePath,
  pickedPaths,
  excludedPaths,
  pickedFolderPaths,
  excludedFolderPaths,
  isSearchResult,
  showSearchResultPath = false,
  showFolderPathSegments,
  onFolderPathSegmentClick,
  onSearchSegmentNavigate,
  onRowClick,
  onTogglePick,
  onTogglePickFolder,
  onToggleExclude,
  onToggleExcludeFolder,
  onNavigateToParent,
  searchPathCasePrefix,
  renderRowPreview,
  renderRowNameSuffix,
  renderRowFileActions,
}: TreeRowProps) {
  const t = useTimePilotT();
  const inverse = useEvidenceLockerAppearance() === 'inverse';
  const tone = lockerTreeClasses(inverse);
  const { extension, pageCount } = nodeMeta(node);
  // Row label only. `node.path` / `pickUrl` keep the `folder_token` segment, so
  // drilling in, picking as a scope and provenance lookups are all unaffected.
  // Relabelled per segment because in flat-folders mode `node.name` is a whole
  // path, not a leaf.
  const caseLabels = useCaseFolderLabels();
  const nodeLabel = relabelCaseFolderSegments(node.name, caseLabels);
  const isDirectory = node.nodeType === 'directory';
  const hasChildren = node.hasChildren === true;
  // ``canDrill`` controls the cursor + keyboard affordance for navigating
  // into a row. Both directories and files (mbox / archive / multi-page PDF)
  // can be drilled into when they have children. In folders mode we
  // synthesize directories with ``hasChildren: false`` to disable
  // navigation, so a single ``hasChildren`` check covers all cases.
  const showClickablePath =
    (showFolderPathSegments && !!onFolderPathSegmentClick) ||
    ((isSearchResult || showSearchResultPath) && !!onSearchSegmentNavigate);
  // Path segments in search / flat-folder rows handle their own clicks; only
  // directories and child-bearing files are drillable at the row level.
  const canDrill =
    isDirectory ||
    hasChildren ||
    (showFolderPathSegments && !!onFolderPathSegmentClick);
  // ``pickUrl`` flows into scope/selection state. Files land in
  // ``selectedEvidence``; folders land in ``selectedScopes`` via
  // ``onTogglePickFolder``. The two intentionally use different URL shapes:
  //
  // - Files run through ``toUrl`` (= ``toProvenanceLookupPath``) so the URL
  //   carries the canonical ``data/bulk/`` prefix that downstream surfaces
  //   need: the ``availableEvidence`` index used by ``renderRowPreview``,
  //   ``SourceCard`` lookups, ``/files/file-info``, ``/files/file-status``,
  //   and ``/api/proxy``.
  // - Folders stay as the raw absolute searchable path because they're only
  //   ever used as filter values (assistant-service ``searchable_path Glob``
  //   matching, chip display) — they never roundtrip through provenance
  //   lookups, so the prefix would only need to be stripped again.
  //
  // For top-level rows ``node.path`` is already absolute; for rows inside a
  // file, ``contextSourceFilePath`` (the source file the user drilled into)
  // provides the missing prefix so subdocuments and virtual directories
  // resolve to the full ``{source.searchablePath}/{relative path}`` shape
  // that tai-processing writes into Turbopuffer.
  //
  // The wire boundary in ``chat.tsx`` runs ``toSearchablePath`` over both
  // arrays before sending them to the assistant-service, so the backend
  // always receives raw searchable paths regardless of the in-memory shape.
  //
  // ``previewUrl`` flows into the SourceCard preview. For subdocuments this
  // is ``fileInfo.subdocument.fileUrl`` (the actual processed ``s3://…`` URI) so the
  // card shows the real storage location, not the lookup-style path.
  const pickUrl = isDirectory
    ? nodeAbsolutePath(node, contextSourceFilePath)
    : nodePickUrl(node, toUrl, contextSourceFilePath);
  const previewUrl = isDirectory ? '' : nodePreviewUrl(node, pickUrl);
  const folderPickable = isDirectory && !!onTogglePickFolder;
  const filePickable = !isDirectory && !!onTogglePick;
  const isPicked = isDirectory
    ? folderPickable && (pickedFolderPaths?.has(pickUrl) ?? false)
    : filePickable && pickedPaths.has(pickUrl);
  const isExcluded = isDirectory
    ? folderPickable && (excludedFolderPaths?.has(pickUrl) ?? false)
    : filePickable && (excludedPaths?.has(pickUrl) ?? false);

  const RowIcon = isDirectory ? FolderOpenIcon : iconForFileNode(node);
  const showSearchPathChrome = showSearchResultPath || isSearchResult;
  const mediaType = (extension ?? node.name.split('.').pop() ?? '').toLowerCase();

  const rowBg = isPicked
    ? tone.rowPicked
    : isExcluded
      ? tone.rowExcluded
      : tone.rowHover;

  return (
    <div
      role={canDrill ? 'button' : undefined}
      tabIndex={canDrill ? 0 : -1}
      onClick={canDrill ? () => onRowClick(node) : undefined}
      onKeyDown={
        canDrill
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onRowClick(node);
              }
            }
          : undefined
      }
      className={`group flex h-full items-center gap-2 rounded-md pl-4 pr-2 ${rowBg} ${
        canDrill ? 'cursor-pointer' : ''
      }`}
    >
      {showClickablePath ? null : (
        <RowIcon size={22} weight="regular" className={`flex-shrink-0 ${tone.fg}`} />
      )}
      {showFolderPathSegments && onFolderPathSegmentClick ? (
        <FolderPathSegments
          path={node.path}
          onSegmentClick={(prefixPath) => onFolderPathSegmentClick(prefixPath)}
        />
      ) : showClickablePath && onSearchSegmentNavigate ? (
        <EvidenceResultPath
          displayPath={searchDisplayPath(node, contextSourceFilePath)}
          node={node}
          contextSourceFilePath={contextSourceFilePath}
          onSegmentNavigate={onSearchSegmentNavigate}
          omitCasePrefix={searchPathCasePrefix}
          leafSuffix={
            !isDirectory
              ? renderRowNameSuffix?.({
                  url: previewUrl,
                  node,
                  mediaType,
                  pageCount: pageCount ?? null,
                })
              : undefined
          }
        />
      ) : (
        <div
          className="min-w-0 flex-1"
          onClick={(event) => {
            // The crumb button opens the row itself. Empty space in this cell
            // must still reach the row handler.
            if (event.target instanceof Element && event.target.closest('button, a')) {
              event.stopPropagation();
            }
          }}
          onKeyDown={(event) => {
            if (
              (event.key === 'Enter' || event.key === ' ') &&
              event.target instanceof Element &&
              event.target.closest('button, a')
            ) {
              event.stopPropagation();
            }
          }}
        >
          <Breadcrumbs
            className="evidence-locker-breadcrumbs min-w-0 w-full"
            tone={inverse ? 'dark' : 'light'}
            aria-label={nodeLabel}
            items={[{ id: node.path || nodeLabel, label: nodeLabel }]}
            onNavigate={() => onRowClick(node)}
            leafSuffix={
              !isDirectory
                ? renderRowNameSuffix?.({
                    url: previewUrl,
                    node,
                    mediaType,
                    pageCount: pageCount ?? null,
                  })
                : undefined
            }
          />
        </div>
      )}

      <div className="flex flex-shrink-0 items-center gap-1">
        {!isDirectory
          ? renderRowFileActions?.({
              url: previewUrl,
              node,
              mediaType,
              pageCount: pageCount ?? null,
            })
          : null}
        {!isDirectory
          ? renderRowPreview?.({
              url: previewUrl,
              node,
              mediaType,
              pageCount: pageCount ?? null,
            })
          : null}
        {isDirectory && folderPickable ? (
          <ScopeCycleButton
            isPicked={isPicked}
            isExcluded={isExcluded}
            itemLabel={nodeLabel}
            onCycle={() => onTogglePickFolder?.(pickUrl, node)}
          />
        ) : filePickable ? (
          <ScopeCycleButton
            isPicked={isPicked}
            isExcluded={isExcluded}
            itemLabel={nodeLabel}
            onCycle={() => onTogglePick(pickUrl, node)}
          />
        ) : null}
        {!isDirectory && (showSearchPathChrome || hasChildren) ? (
          <Button
            type="button"
            iconOnly
            size="sm"
            style="text"
            color={inverse ? 'light' : 'subdued'}
            background={inverse ? 'inverse' : 'default'}
            onClick={(e) => {
              e.stopPropagation();
              if (showSearchPathChrome) {
                onNavigateToParent(node);
              } else {
                onRowClick(node);
              }
            }}
            className={`flex h-7 w-7 shrink-0 items-center justify-center ${tone.fg}`}
            aria-label={t(showSearchPathChrome
              ? 'chat.evidence.tree.parentFolder'
              : 'chat.evidence.tree.browseContents')}
            title={t(showSearchPathChrome
              ? 'chat.evidence.tree.parentFolder'
              : 'chat.evidence.tree.browseContents')}
          >
            <CaretRightIcon weight="bold" className={tone.fg} />
          </Button>
        ) : null}
      </div>
    </div>
  );
}
